// axis-aligned 2d bounding box

import { Vector3 } from "three";
import { ModelInstance } from "../modelInstance/ModelInstance";

export class AABB {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;

    constructor(minX: number, maxX: number, minY: number, maxY: number) {
        this.minX = minX;
        this.maxX = maxX;
        this.minY = minY;
        this.maxY = maxY;
    }

    get width() {
        return this.maxX - this.minX;
    }

    get height() {
        return this.maxY - this.minY;
    }

    get center() {
        return { x: (this.minX + this.maxX) / 2, y: (this.minY + this.maxY) / 2 };
    }

    get corners() {
        return [
            { x: this.minX, y: this.minY },
            { x: this.maxX, y: this.minY },
            { x: this.minX, y: this.maxY },
            { x: this.maxX, y: this.maxY },
        ];
    }

    get area() {
        return this.width * this.height;
    }

    rotate90() {
        const center = this.center;
        const width = this.width;
        const height = this.height;

        return new AABB(
            center.y - height / 2,
            center.y + height / 2,
            center.x - width / 2,
            center.x + width / 2
        );
    }

    moveTo(x: number, y: number) {
        const width = this.width;
        const height = this.height;

        return new AABB(x, x + width, y, y + height);
    }
}

export const getAabbFromRect = (
    rect: { min: Vector3; max: Vector3 },
    axis: "xy" | "xz" | "yz" = "xz"
): AABB => {
    const c1 = axis === "xy" || axis === "xz" ? "x" : "y";
    const c2 = axis === "xz" || axis === "yz" ? "z" : "y";

    return new AABB(
        Math.min(rect.min[c1], rect.max[c1]),
        Math.max(rect.min[c1], rect.max[c1]),
        Math.min(rect.min[c2], rect.max[c2]),
        Math.max(rect.min[c2], rect.max[c2])
    );
};

export const getModelInstanceAabb = (
    instance: Pick<ModelInstance, "box" | "position" | "rotation">
) => {
    let aabb = getAabbFromRect(instance.box);

    const existingRotation = instance.rotation;
    const is90Rotation = existingRotation === Math.PI / 2 || existingRotation === -Math.PI / 2;
    if (is90Rotation) {
        aabb = aabb.rotate90();
    }

    aabb = aabb.moveTo(instance.position.x, instance.position.y);

    return aabb;
};
